import { getPublicProgramme, preloadPublicProgramProjets } from '../services/siteContentService';
import { preloadImage, injectDocumentPreload, normalizeStorageUrl } from '../config/apiBase';
import {
  fastestHeroUrl,
  getProgramHeroAssets,
  normalizeHeroAssets,
  PRIORITY_PROGRAM_SLUGS,
  writeProgramHeroCache,
} from '../config/programHeroImages';

const startedSlugs = new Set();
const inflightDetail = new Set();

const preloadHeroAssets = (assets) => {
  const fastest = fastestHeroUrl(assets);
  if (!fastest) return;
  if (assets.image_url_webp_sm && assets.image_url_webp) {
    injectDocumentPreload(fastest, {
      imageSrcSet: `${assets.image_url_webp_sm} 960w, ${assets.image_url_webp} 1600w`,
      imageSizes: '100vw',
    });
  } else {
    injectDocumentPreload(fastest);
  }
  void preloadImage(fastest);
};

/** Précharge l’image hero CMS d’un programme (WebP 960 px en priorité). */
export function prefetchProgramHero(slug, knownProgram) {
  if (!slug) return;

  const known = typeof knownProgram === 'string'
    ? { image_url: knownProgram }
    : knownProgram || {};
  const cached = getProgramHeroAssets(slug);
  const knownAssets = normalizeHeroAssets(known);
  const imageChanged = Boolean(
    knownAssets.image_url && cached.image_url && knownAssets.image_url !== cached.image_url
  );
  if (imageChanged) {
    startedSlugs.delete(slug);
  }
  const assets = imageChanged
    ? knownAssets
    : normalizeHeroAssets({
        image_url: known.image_url || cached.image_url,
        image_url_webp: known.image_url_webp || cached.image_url_webp,
        image_url_webp_sm: known.image_url_webp_sm || cached.image_url_webp_sm,
      });

  if (assets.image_url || assets.image_url_webp) {
    writeProgramHeroCache(slug, { ...known, ...cached, ...assets });
    preloadHeroAssets(assets);
  }

  if (assets.image_url_webp_sm || startedSlugs.has(slug)) {
    startedSlugs.add(slug);
    return;
  }

  if (inflightDetail.has(slug)) return;
  inflightDetail.add(slug);
  startedSlugs.add(slug);

  void getPublicProgramme(slug)
    .then((data) => {
      if (!data?.image_url && !data?.image_url_webp) return;
      writeProgramHeroCache(slug, data);
      preloadHeroAssets(normalizeHeroAssets(data));
    })
    .catch(() => {
      startedSlugs.delete(slug);
    })
    .finally(() => {
      inflightDetail.delete(slug);
    });
}

export function prefetchPriorityProgramHeroes(programmes) {
  const bySlug = new Map((Array.isArray(programmes) ? programmes : []).map((p) => [p.slug, p]));
  const ordered = [
    ...PRIORITY_PROGRAM_SLUGS,
    ...[...bySlug.keys()].filter((slug) => !PRIORITY_PROGRAM_SLUGS.includes(slug)),
  ];
  ordered.forEach((slug) => {
    prefetchProgramHero(slug, bySlug.get(slug));
  });
}

export const PROJET_CARD_SIZES = '(max-width: 768px) 100vw, 360px';
const PREFETCH_CARD_LIMIT = 6;

export const fastestProjetCardUrl = (projet) =>
  normalizeStorageUrl(
    projet?.image_url_webp_card
    || projet?.image_url_webp_sm
    || projet?.image_url_webp
    || projet?.image_url
  );

export function prefetchProjetCardImages(projets, limit = PREFETCH_CARD_LIMIT) {
  if (!Array.isArray(projets) || projets.length === 0) return;
  projets.slice(0, limit).forEach((projet) => {
    const card = normalizeStorageUrl(projet?.image_url_webp_card);
    const sm = normalizeStorageUrl(projet?.image_url_webp_sm);
    const fastest = fastestProjetCardUrl(projet);
    if (!fastest) return;
    const srcSet = [
      card && `${card} 480w`,
      sm && `${sm} 960w`,
    ].filter(Boolean).join(', ');
    void preloadImage(fastest, {
      fetchPriority: 'low',
      imageSrcSet: srcSet || undefined,
      imageSizes: srcSet ? PROJET_CARD_SIZES : undefined,
    });
  });
}

/** Précharge les projets d'un programme (liste légère + premières photos). */
export function prefetchProgramProjets(slug) {
  if (!slug) return;
  void preloadPublicProgramProjets(slug).then((data) => {
    prefetchProjetCardImages(data);
    return data;
  }).catch(() => null);
}
